import { CommonModule } from "@angular/common";
import { CUSTOM_ELEMENTS_SCHEMA, Component, OnDestroy, ViewChild, inject } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { IonContent, IonicModule } from "@ionic/angular";
import { TranslateModule, TranslateService } from "@ngx-translate/core";

import { GlobalVarsModel } from "@models/ModelGlobalsVar";
import { UtilsMsoService } from "@mso-services/utilsMso.service";
import { EventsService } from "@providers/EventsService";
import { TrackingService } from "@providers/TrackingService";
import { UserService } from "@providers/UserService";
import { NewBaseService } from "@providers/newBase.service";
import { DeviceService } from "@services/device.service";
import { SportsFreeBetsService } from "@sports-services/sports.free-bets.service";

import { MSO_PATHS } from "@shared-constants/routes";
import { Utils } from "@sports-utils/Utils";
import { SportsConstants } from "@sports-utils/index";
import { Subject, of, switchMap, takeUntil } from "rxjs";

import { FreeBetsHeader, FreeBetsInfo, FreeBetsVerification } from "@sports-components/index";

@Component({
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  selector: "free-bets-page",
  templateUrl: "free-bets.page.html",
  standalone: true,
  imports: [
    IonicModule,
    CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    FormsModule,
    FreeBetsHeader,
    FreeBetsVerification,
    FreeBetsInfo,
  ],
})
export class FreeBetsPage implements OnDestroy {
  @ViewChild(IonContent) content: IonContent;
  imageSource: string;
  codereCardActive = true;

  activeFBOnline;
  activeFBLocal;

  aPendingFBOnline;
  aPendingFBLocal;

  phoneNumber: string;

  firstLoad = true;
  forbiddenFreebets = false;

  hasAPendingFBOnline = false;
  hasActiveFBOnline = false;
  hasAPendingFBLocal = false;
  hasActiveFBLocal = false;

  showFbOnline: boolean = true;
  showFbLocal: boolean;

  globalVars: GlobalVarsModel;

  private readonly unsubscribe$ = new Subject();

  trackingService = inject(TrackingService);
  userService = inject(UserService);
  translate = inject(TranslateService);
  utils = inject(Utils);
  router = inject(Router);
  deviceService = inject(DeviceService);
  newBaseService = inject(NewBaseService);
  eventService = inject(EventsService);
  utilsMsoService = inject(UtilsMsoService);
  sportsFreebetsService = inject(SportsFreeBetsService);

  isMobile = false;
  isDesktop = false;

  constructor() {
    this.isDesktop = this.deviceService.isDesktop();
    this.isMobile = this.deviceService.isMobile();

    this.activeFBOnline = [];
    this.activeFBLocal = [];

    this.aPendingFBOnline = [];
    this.aPendingFBLocal = [];

    this.newBaseService.getVars
      .pipe(
        switchMap((data: GlobalVarsModel) => {
          this.globalVars = data;
          this.codereCardActive = this.globalVars.user.codereCard.active;
          return of(null);
        }),
      )
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe();

    this.updateView();
  }

  updateView() {
    this.hasAPendingFBOnline = !!this.aPendingFBOnline?.length;
    this.hasActiveFBOnline = !!this.activeFBOnline?.length;
    this.hasAPendingFBLocal = !!this.aPendingFBLocal?.length;
    this.hasActiveFBLocal = !!this.activeFBLocal?.length;
  }

  onChangeHeader(fbType) {
    this.showFbOnline = fbType === SportsConstants.FREEBET_TYPE.ONLINE;
    this.showFbLocal = fbType === SportsConstants.FREEBET_TYPE.PRESENCIAL;
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }

  ionViewWillEnter() {
    this.trackingService.trackEvent(["AccesToFreeBets", "", "", "Acceder FreeBets", "event"]);
    this.eventService.publish("footer:refresh", this.content);
  }

  ionViewDidEnter() {
    this.updateData();
  }

  ionViewDidLoad() {
    this.eventService.publish("footer:refresh", this.content);
  }

  updateData() {
    this.utils.loader();
    this.sportsFreebetsService
      .getFreeBets()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: async (data) => {
          if (!data.success) {
            if (data.pendingSms) {
              this.utilsMsoService
                .getUserData()
                .pipe(takeUntil(this.unsubscribe$))
                .subscribe({
                  next: (userData) => {
                    this.eventService.publish("loader:dismiss", null);
                    if (!userData.sessionOut) {
                      this.phoneNumber = userData.NumMovil;
                    } else {
                      //disconnect the user
                      this.eventService.publish("user:sessionTimeout");
                    }
                  },
                  error: () => {
                    this.eventService.publish("loader:dismiss", null);
                    this.utils.showError(this.translate.instant("m_menu_serverFail"));
                  },
                });
            } else {
              this.eventService.publish("loader:dismiss", null);
              this.showAlertForForbiddenAction();
            }
            this.forbiddenFreebets = true;
          } else {
            this.eventService.publish("loader:dismiss", null);
            this.forbiddenFreebets = false;
            this.firstLoad = false;
            const splittedFB = this.sportsFreebetsService.splitFbToOnlineAndLocal(
              data.availableDepositFbs,
              data.data,
            );
            this.activeFBOnline = splittedFB.activeFBOnline;
            this.activeFBLocal = splittedFB.activeFBLocal;
            this.aPendingFBOnline = splittedFB.aPendingFBOnline;
            this.aPendingFBLocal = splittedFB.aPendingFBLocal;
          }
          this.updateView();
          this.eventService.publish("footer:refresh", this.content);
          await this.utils.closeLoader();
        },
        error: async () => {
          this.updateView();
          this.utils.showError(this.translate.instant("m_menu_serverFail"));
          await this.utils.closeLoader();
        },
      });
  }

  gotoNearLocal() {
    this.router.navigate(["NearestLocalPage", {}], {
      queryParams: { animate: false },
    });
  }

  checkCanSendCodereCard() {
    this.userService.checkCanSendCodere().subscribe(
      (data) => {
        if (!data.success) {
          this.userService.hasDeposit().subscribe(
            (depositData) => {
              if (depositData.success) {
                this.router.navigate(["CardRequestFormPage", {}], {
                  queryParams: { animate: false },
                });
              } else {
                this.eventService.publish("user:serverfail");
              }
            },
            () => {
              this.eventService.publish("user:serverfail:soft");
            },
          );
        } else {
          this.utils.confirm(
            false,
            this.translate.instant("tFail"),
            this.translate.instant("coderecardverifdocum"),
            this.translate.instant("tCancel"),
            () => {
              //
            },
            this.translate.instant("check"),
            () => {
              setTimeout(() => {
                this.router.navigate(["DocumentVerificationPage", {}]);
              }, 800);
            },
          );
        }
      },
      () => {
        this.utils.showError(this.translate.instant("GetCardOnlylink_send"), null);
      },
    );
  }

  accessToCheckCodereCard() {
    this.userService.checkCanSendCodere().subscribe(
      (data) => {
        if (!data.success) {
          this.router.navigate([`/${MSO_PATHS.BASE}/${MSO_PATHS.CardLinkPage}`]);
        } else {
          this.utils.confirm(
            false,
            this.translate.instant("tFail"),
            this.translate.instant("coderecardverifdocum"),
            this.translate.instant("tCancel"),
            () => {
              //
            },
            this.translate.instant("check"),
            () => {
              setTimeout(() => {
                this.router.navigate(["DocumentVerificationPage", {}]);
              }, 800);
            },
          );
        }
      },
      () => {
        this.utils.showError(this.translate.instant("GetCardOnlylink"), null);
      },
    );
  }

  showAlertForForbiddenAction() {
    this.utils.alert(
      true,
      this.translate.instant("Attention"),
      this.translate.instant("SPO_FB_ForbiddenAction"),
      this.translate.instant("tAccept"),
      () => {
        this.router.navigate(["/"]);
      },
    );
  }
}
